import {
  ChevronsUpDown,
  CircleUser,
  CircleUserRound,
  FilesIcon,
  LogOut,
  UserPen,
} from 'lucide-react'
import PropTypes from 'prop-types'
import { Link } from 'react-router'

import { Avatar, AvatarFallback } from '@/components/elements/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/elements/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/elements/sidebar'
import { logout } from '@/services/user.service'

const UserAvatar = ({ user }) => {
  const hasName = user.firstName && user.lastName

  return (
    <>
      <Avatar>
        <AvatarFallback>
          {hasName ? (
            `${user.firstName.substring(0, 1)} ${user.lastName.substring(0, 1)}`
          ) : (
            <CircleUserRound className="stroke-primary mx-auto my-auto stroke-1" />
          )}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight">
        <span className="truncate">{hasName && `${user.firstName} ${user.lastName}`}</span>
        <span className="truncate text-xs">{user.email}</span>
      </div>
      <ChevronsUpDown className="ml-auto size-4" />
    </>
  )
}

UserAvatar.propTypes = {
  user: PropTypes.object.isRequired,
}

export function NavUser({ clearStore, user }) {
  const { isMobile, setOpen } = useSidebar()

  // Find the user's entity_role for the active entity so we can deep-link to
  // their entity-scoped profile page. Falls back to null when the user has no
  // active entity (e.g. an Xverse-only admin).
  const activeRole = user?.activeEntity
    ? (user?.entityRoles ?? []).find((r) => r?.entity?.id === user.activeEntity)
    : null

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
            >
              <UserAvatar user={user} />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuGroup>
              {activeRole && (
                <DropdownMenuItem asChild>
                  <Link to={`/accounts/${activeRole.entity.id}/users/${activeRole.id}`}>
                    <CircleUser />
                    Profile
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to="/profile">
                  <UserPen />
                  Account Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/certificates">
                  <FilesIcon />
                  My Certificates
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                setOpen(false)
                logout(user.refreshToken, clearStore)
              }}
            >
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

NavUser.propTypes = {
  clearStore: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
}
